import React, {useEffect, useState} from "react";
import {makeStyles, Typography} from "@material-ui/core";
import {
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from "@material-ui/lab";
import CheckIcon from "@material-ui/icons/Check";
import {generateTimeRemainingMessage} from "common/utils/generate_time_remaining_message";
import {useInterpolatedValue} from "./use_interpolated_value";

const useStyles = makeStyles({
  "@keyframes pulse": {
    "0%, 100%": {opacity: 1},
    "50%": {opacity: 0},
  },
  pulsing: {
    animation: "$pulse 1.2s ease-in-out infinite",
  },
  missingOppositeContent: {
    "&:before": {
      display: "none",
    },
  },
  item: {
    minHeight: 40,
  },
  content: {
    padding: "0px 0px 16px 16px",
  },
});

const COMPLETION_ANIMATION_MS = 2000;

export const FinishedTimelineItem = ({title}) => {
  const classes = useStyles();
  return (
    <TimelineItem
      classes={{
        missingOppositeContent: classes.missingOppositeContent,
        root: classes.item,
      }}
    >
      <TimelineSeparator>
        <TimelineDot
          style={{padding: 0, backgroundColor: "limegreen", boxShadow: "none"}}
        >
          <CheckIcon style={{fontSize: 9}} />
        </TimelineDot>
        <TimelineConnector />
      </TimelineSeparator>
      <TimelineContent className={classes.content}>
        <Typography variant="subtitle1">{title}</Typography>
      </TimelineContent>
    </TimelineItem>
  );
};

export const PendingTimelineItem = ({title, connector = true}) => {
  const classes = useStyles();
  return (
    <TimelineItem
      classes={{
        missingOppositeContent: classes.missingOppositeContent,
        root: classes.item,
      }}
    >
      <TimelineSeparator>
        <TimelineDot style={{boxShadow: "none"}} />
        {connector && <TimelineConnector />}
      </TimelineSeparator>
      <TimelineContent className={classes.content}>
        <Typography variant="subtitle1">{title}</Typography>
      </TimelineContent>
    </TimelineItem>
  );
};

export const InProgressTimelineItem = ({
  title,
  description,
  percentComplete,
  timeEstimate,
  rateMessage,
  refreshTime,
  connector = true,
}) => {
  const classes = useStyles();
  const interpolatedPercent = refreshTime
    ? useInterpolatedValue(percentComplete ?? 0, refreshTime)
    : percentComplete;
  return (
    <TimelineItem
      classes={{
        missingOppositeContent: classes.missingOppositeContent,
        root: classes.item,
      }}
    >
      <TimelineSeparator>
        <TimelineDot
          color="primary"
          className={classes.pulsing}
          style={{boxShadow: "none"}}
        />
        {connector && <TimelineConnector />}
      </TimelineSeparator>
      <TimelineContent className={classes.content}>
        <Typography variant="subtitle1">
          {title} {`${Math.round(interpolatedPercent)}%`}
        </Typography>
        {description && (
          <Typography variant="body2" style={{color: "#888888ff"}}>
            {description}
          </Typography>
        )}
        {(timeEstimate || rateMessage) && (
          <Typography
            style={{fontStyle: "italic", color: "#888888ff"}}
            variant="body2"
          >
            {generateTimeRemainingMessage(timeEstimate) + (rateMessage ?? "")}
          </Typography>
        )}
        {interpolatedPercent !== null && (
          <div
            style={{
              marginTop: 4,
              height: 3,
              width: "100%",
              backgroundColor: "#ccc",
            }}
          >
            <div
              style={{
                height: "100%",
                backgroundColor: "#1f88e5",
                width: `${interpolatedPercent}%`,
              }}
            />
          </div>
        )}
      </TimelineContent>
    </TimelineItem>
  );
};

export const StageTimelineItem = ({
  title,
  description,
  isActive,
  isComplete,
  percentComplete,
  timeRemaining,
  refreshTime,
  isUnlocked,
  onVisuallyComplete,
  connector = true,
}) => {
  const [visuallyComplete, setVisuallyComplete] = useState(isComplete);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    if (isComplete && isUnlocked && !visuallyComplete) {
      setCompleting(true);
      const timer = setTimeout(() => {
        setVisuallyComplete(true);
        setCompleting(false);
        onVisuallyComplete();
      }, COMPLETION_ANIMATION_MS);
      return () => clearTimeout(timer);
    }
  }, [isComplete, isUnlocked]);

  if (visuallyComplete) {
    return <FinishedTimelineItem title={title} />;
  }
  if ((isActive || completing) && isUnlocked) {
    return (
      <InProgressTimelineItem
        title={title}
        description={description}
        percentComplete={completing ? 100 : percentComplete}
        timeEstimate={timeRemaining}
        refreshTime={completing ? COMPLETION_ANIMATION_MS : refreshTime}
        connector={connector}
      />
    );
  }
  return <PendingTimelineItem title={title} />;
};
